import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {MetaTitle} from '../../../../decorator/meta-title';
import {OfferService} from '../../../../services/offer/offer.service';
import {Offer} from '../../../../models/offer';
import { NgClass } from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';
import {CurrencyPipe} from '../../../../pipes/currency/currency.pipe';
import {Paginator} from '../../../../tools/pagination/paginator';
import {PaginatorComponent} from '../../../layout/paginator/paginator.component';
import {PartnerOffer} from '../../../../models/partner-offer';
import {PartnerOfferService} from '../../../../services/partner-offer/partner-offer.service';
import {AuthService} from '../../../../services/auth/auth.service';
import {Partner} from '../../../../models/partner';
import {forkJoin} from 'rxjs';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {ImageService} from '../../../../services/image/image.service';
import {RouterLink} from '@angular/router';
import {LoaderService} from '../../../../services/loader/loader.service';
import {DropdownSelectComponent} from '../../../layout/dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../../../layout/dropdown-select-option/dropdown-select-option.component';
import {Country} from '../../../../models/country';
import {ConstantList} from '../../../../models/constant-list';
import {CustomPayout} from '../../../../models/custom-payout';
import {CustomPayoutService} from '../../../../services/custom-payout/custom-payout.service';
import {StatisticService} from '../../../../services/statistic/statistic.service';
import {FilterStorageService} from '../../../../services/filter-storage/filter-storage.service';

@Component({
  selector: 'app-offer-list',
  imports: [
    TranslatePipe,
    CurrencyPipe,
    NgClass,
    PaginatorComponent,
    ReactiveFormsModule,
    RouterLink,
    DropdownSelectComponent,
    DropdownSelectOptionComponent
],
  templateUrl: './offer-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './offer-list.component.scss'
})
@MetaTitle('Offer list')
export class OfferListComponent implements OnInit {

  filters = new FormGroup({
    name: new FormControl(''),
    conversionType: new FormControl(''),
    verticals: new FormControl([]),
    countries: new FormControl([]),
    requireApproval: new FormControl(''),
    active: new FormControl(false)
  });

  connectedUser: Partner | null = null;
  offers: Offer[] = [];
  expandedCountries: { [index: number]: boolean } = {};
  partnerOffers: PartnerOffer[] = [];
  customPayouts: CustomPayout[] = [];
  customPayoutByOffer: { [offerId: number]: CustomPayout | null } = {};
  usedOfferIds: Set<number> = new Set<number>();

  countries: Country[] = [];
  verticalList: ConstantList[] = [];
  conversionTypeList: string[] = [];

  paginator: Paginator = new Paginator();

  constructor(
    protected imageService: ImageService,
    private auth: AuthService,
    private loader: LoaderService,
    private offerService: OfferService,
    private partnerOfferService: PartnerOfferService,
    private customPayoutService: CustomPayoutService,
    private statisticService: StatisticService,
    private filterStorage: FilterStorageService,
  ) {
  }

  ngOnInit(): void {
    const savedFilters = this.filterStorage.load('offers');
    if (savedFilters) {
      this.filters.patchValue(savedFilters);
    }

    this.loader.startLoading();

    forkJoin([
      this.auth.getConnectedUser(),
      this.auth.getAsyncConfig(),
    ]).subscribe(([connectedUser, config]) => {
      if (connectedUser && connectedUser.id) {
        this.connectedUser = connectedUser;
        this.countries = config?.countries ?? [];
        this.conversionTypeList = config?.data?.conversionTypeList;
        this.verticalList = config?.data?.verticalList.map((vertical: any) => new ConstantList().deserialize(vertical));

        forkJoin([
          this.partnerOfferService.findAll(),
          this.customPayoutService.findAll(),
          this.statisticService.fetchUsedOfferIds(),
        ]).subscribe(([partnerOffers, customPayouts, usedOfferIds]) => {
          this.partnerOffers = partnerOffers;
          this.customPayouts = customPayouts;
          this.usedOfferIds = new Set(usedOfferIds);

          this.fetchAll();

          this.loader.stopLoading();
        });
      }
    });
  }

  fetchAll(): void {
    this.filterStorage.save('offers', this.filters.value);

    const data: any = {groups: 'product,goals,goal,goalTypes,goalType', sort: 'name'};
    if (this.connectedUser) {
      data['partnerId'] = this.connectedUser.id;
    }

    for (const [index, value] of Object.entries(this.filters.value)) {
      if (value === false || value === null) {
        continue;
      }

      if ((value !== '' && !Array.isArray(value)) || (Array.isArray(value) && value.length)) {
        data[index] = value;
      }
    }

    this.offerService.findAll(data, this.paginator).subscribe((offers: Offer[]) => {
      this.offers = offers;

      this.customPayoutByOffer = {};
      for (const offer of offers) {
        if (offer.id) {
          this.customPayoutByOffer[offer.id] = offer.getCustomPayout(this.customPayouts);
        }
      }
    });
  }

  getCustomPayout(offer: Offer): CustomPayout | null {
    return offer.id ? this.customPayoutByOffer[offer.id] ?? null : null;
  }

  isUsedOffer(offer: Offer): boolean {
    return offer.id !== null && this.usedOfferIds.has(offer.id);
  }

  getVerticalName(verticalId: number): string {
    const vertical = this.verticalList.find((vertical: ConstantList) => Number(vertical.id) === Number(verticalId));

    return 'vertical.' + (vertical?.identifier ?? '');
  }

  getOfferStatus(offer: Offer): string {
    const partnerOffer = this.partnerOffers.find((data: PartnerOffer) => data.offer?.id === offer.id);
    if (partnerOffer) {
      return partnerOffer.status;
    }

    return offer.requireApproval && !this.connectedUser?.allOffersAccess ? 'apply_to_run' : 'public';
  }

  getOfferStatusClass(offer: Offer): string {
    const status = this.getOfferStatus(offer);

    switch (status) {
      case 'pending':
        return 'bg-warning';
      case 'apply_to_run':
        return 'bg-primary';
      case 'active':
        return 'bg-success';
      default:
        return 'bg-dark';
    }
  }

}
